import { useEffect, useState } from 'react';
import { supabaseClient } from '../config/supabase-client';

export interface CollageLink {
    collage_id: string;
    item_alt: string;
    link: string;
}

/**
 * Fetches link overrides from the `collage_links` table.
 * Returns a map of `"collage_id::item_alt"` -> link URL for fast lookup.
 */
export function useCollageLinks() {
    const [linkMap, setLinkMap] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function fetchLinks() {
            const { data, error } = await supabaseClient
                .from('collage_links')
                .select('collage_id, item_alt, link');

            if (error) {
                console.error('Failed to fetch collage links:', error.message);
                setLoading(false);
                return;
            }

            if (!cancelled && data) {
                const map: Record<string, string> = {};
                for (const row of data) {
                    map[`${row.collage_id}::${row.item_alt}`] = row.link;
                }
                setLinkMap(map);
            }

            if (!cancelled) setLoading(false);
        }

        fetchLinks();
        return () => { cancelled = true; };
    }, []);

    /** Upsert a single link override. Requires authenticated session. */
    async function updateLink(collage_id: string, item_alt: string, link: string): Promise<string | null> {
        const { error } = await supabaseClient
            .from('collage_links')
            .upsert({ collage_id, item_alt, link, updated_at: new Date().toISOString() }, {
                onConflict: 'collage_id,item_alt',
            });

        if (error) return error.message;

        setLinkMap(prev => ({ ...prev, [`${collage_id}::${item_alt}`]: link }));
        return null;
    }

    return { linkMap, loading, updateLink };
}
